<template>
  <div class="experience-card" ref="cardRef">
    <div class="experience-card__counter">
      <div class="counter-wrapper">
        <div class="counter-track" ref="counterTrack">
          <span v-for="num in displayNumbers" :key="num" class="counter-digit">
            {{ num }}
          </span>
        </div>
      </div>
      <span class="experience-card__plus">+</span>
    </div>
    <p class="experience-card__label" v-html="$t('page.about.experienceCard.label')"></p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'

// Years of experience calculation (started in 2016)
const START_YEAR = 2016
const yearsOfExperience = computed(() => {
  const currentYear = new Date().getFullYear()
  return currentYear - START_YEAR
})

// Generate array of numbers from 0 to target for slot machine effect
const displayNumbers = computed(() => {
  const nums = []
  for (let i = 0; i <= yearsOfExperience.value; i++) {
    nums.push(i)
  }
  return nums
})

const cardRef = ref(null)
const counterTrack = ref(null)
let hasAnimated = false
let observer = null

const animateCounter = () => {
  if (hasAnimated || !counterTrack.value) return
  hasAnimated = true

  const target = yearsOfExperience.value
  const digitHeight = 72 // Height of each digit in pixels
  const targetY = -(target * digitHeight)

  // Animate the track sliding up like a slot machine
  gsap.fromTo(
    counterTrack.value,
    { y: 0 },
    {
      y: targetY,
      duration: 2,
      ease: 'power3.out',
      onUpdate: function () {
        // Add slight blur during fast movement
        const progress = this.progress()
        const velocity = Math.abs(this.ratio - (this._prevRatio || 0)) * 100
        this._prevRatio = this.ratio

        if (counterTrack.value) {
          const blur = Math.min(velocity * 2, 3)
          counterTrack.value.style.filter = `blur(${blur}px)`
        }
      },
      onComplete: () => {
        if (counterTrack.value) {
          counterTrack.value.style.filter = 'blur(0px)'
        }
      },
    }
  )
}

const setupObserver = () => {
  if (!cardRef.value) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCounter()
          observer?.disconnect()
        }
      })
    },
    { threshold: 0.5 }
  )

  observer.observe(cardRef.value)
}

onMounted(() => {
  setupObserver()
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.experience-card {
  @apply h-full flex flex-col items-center justify-center p-4 text-center;
}

.experience-card__counter {
  @apply flex items-baseline;
}

.counter-wrapper {
  @apply relative overflow-hidden;
  height: 72px;
}

.counter-track {
  @apply flex flex-col;
  will-change: transform, filter;
}

.counter-digit {
  @apply text-7xl font-black leading-none flex items-center justify-center;
  height: 72px;
  background: linear-gradient(135deg, var(--color-electric-purple) 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.experience-card__plus {
  @apply text-4xl font-bold ml-0.5;
  color: var(--color-electric-purple);
}

.experience-card__label {
  @apply text-sm mt-2;
  color: var(--color-text-secondary);
}
</style>
